import { observer } from "mobx-react-lite";
import React, { useContext, useEffect } from "react";

import DateInput from "../components/DateInput";
import Select from "../components/Select";
import TextInput from "../components/TextInput";
import { ApiContext, NavigationContext, UserContext } from "../contexts";
import {
  IS_DEV,
  LAND_SITUATION_REGISTERED,
  LAND_SITUATION_UNPURCHASED,
  LAND_SITUATION_UNREGISTERED,
} from "../resources/constants";
import { LandType } from "../types";
import { scrollToTop } from "../util";

const YourLandScreen = observer(() => {
  const apiContext = useContext(ApiContext);
  const userContext = useContext(UserContext);
  const navContext = useContext(NavigationContext);
  useEffect(() => {
    scrollToTop();
    navContext.setFooterNavVisible(true);
    navContext.setNextButtonVisible(true);
    navContext.setLoginButtonVisible(false);
    navContext.setBackButtonVisible(true);
    navContext.setNextButtonVisible(true);
    navContext.setRestartButtonVisible(true);

    navContext.setOnNavigateBack(() => {
      // Reset the form values when the user goes back
      apiContext.resetLandFilters();
    });

    navContext.resetNextBtn();
    navContext.setNextButtonEnabled(IS_DEV);

    if (apiContext.enteredViaHome) {
      apiContext.setLotWidth(apiContext.enteredViaHome.lot_width.toString(10));
      apiContext.setHouseType(apiContext.enteredViaHome.house_type);
    }

    return () => {
      // Unbind the handler when we unmount
      navContext.resetOnNavigateBack();
    };
  }, []);

  useEffect(() => {
    const isLandSituationValid =
      !!userContext.landSituation &&
      (userContext.landSituation !== LAND_SITUATION_UNREGISTERED ||
        userContext.landRegistrationDate.trim() !== "");
    // validate fields when they change
    let nextEnabled = IS_DEV;
    if (
      userContext.landType === LandType.KNOCK_DOWN_REBUILD &&
      apiContext.region !== null &&
      apiContext.lotWidth !== ""
    ) {
      nextEnabled = true;
    } else if (
      userContext.landType === LandType.VACANT_BLOCK &&
      apiContext.region !== null &&
      apiContext.estate !== null &&
      apiContext.estate.id !== "other" &&
      apiContext.lotWidth !== "" &&
      isLandSituationValid
    ) {
      nextEnabled = true;
    } else if (
      userContext.landType === LandType.VACANT_BLOCK &&
      apiContext.region !== null &&
      apiContext.estate !== null &&
      apiContext.estate.id === "other" &&
      apiContext.estateOther.trim() !== "" &&
      apiContext.lotWidth !== "" &&
      isLandSituationValid
    ) {
      nextEnabled = true;
    }
    navContext.setNextButtonEnabled(nextEnabled);
  }, [
    apiContext.region,
    apiContext.estate,
    apiContext.estateOther,
    apiContext.lotWidth,
    userContext.landSituation,
    userContext.landRegistrationDate,
  ]);

  const getRegionOptions = () => {
    const options = [{ key: "", value: "Please select region" }];
    for (const region of apiContext.regionResults) {
      options.push({
        key: region.id,
        value: region.title,
      });
    }
    return options;
  };

  const getEstateOptions = () => {
    const options = [{ key: "", value: "Please select estate" }];

    if (apiContext.region !== null) {
      for (const estate of apiContext.region.estates) {
        options.push({
          key: estate.id,
          value: estate.title,
        });
      }
    }

    options.push({ key: "other", value: "Other" });
    return options;
  };

  const getLotWidthOptions = () => {
    const options = [{ key: "", value: "Lot width" }];
    for (const width of apiContext.lotWidthsResults) {
      options.push({
        key: width,
        value: `${width}m`,
      });
    }
    return options;
  };

  const getLandSituationOptions = () => {
    const options = [{ key: "", value: "Please tell us about your land" }];
    for (const option of [
      LAND_SITUATION_REGISTERED,
      LAND_SITUATION_UNREGISTERED,
      LAND_SITUATION_UNPURCHASED,
    ]) {
      options.push({
        key: option,
        value: option,
      });
    }
    return options;
  };

  const onChangeRegion = (regionId: string) => {
    if (apiContext.region !== null && apiContext.region.id !== regionId) {
      apiContext.setEstate(null);
      apiContext.setEstateOther("");
    }
    apiContext.setRegion(apiContext.regionsKeyedById[regionId]);
  };

  const onChangeEstate = (estateId: string) => {
    if (apiContext.region !== null && estateId !== "other") {
      apiContext.setEstateOther("");
      for (const estate of apiContext.region.estates) {
        if (estate.id === estateId) {
          apiContext.setEstate(estate);
        }
      }
    } else if (estateId === "other") {
      apiContext.setEstate({
        id: "other",
        title: "Other",
        site_cost: null,
      });
    }
  };

  const onChangeLandSituation = (option: string) => {
    if (option !== LAND_SITUATION_UNREGISTERED) {
      userContext.setLandRegistrationDate("");
    }
    userContext.setLandSituation(option);
  };

  return (
    <div className={"screen screen-your-land container"}>
      <h2 className="h1">{"Your Land"}</h2>
      <p className={"subtitle"}>
        Please tell us about your block of land. This will allow us to suggest
        suitable home designs.
      </p>
      <form className={"your-land-form"}>
        <div className={"form-group"}>
          <Select
            id={"select-region"}
            options={getRegionOptions()}
            value={apiContext.region ? apiContext.region.id.toString() : ""}
            onChange={onChangeRegion}
          />
          {userContext.landType === LandType.VACANT_BLOCK && (
            <>
              <Select
                id={"select-estate"}
                disabled={apiContext.region === null}
                options={getEstateOptions()}
                value={apiContext.estate ? apiContext.estate.id : ""}
                onChange={(value) => onChangeEstate(value)}
              />
              <TextInput
                disabled={
                  apiContext.estate ? apiContext.estate.id !== "other" : true
                }
                placeholder={"(If Other) Please Specify"}
                value={apiContext.estateOther}
                onChange={(value) => apiContext.setEstateOther(value)}
                onScreenKeyboard={userContext.isKioskMode}
              />
            </>
          )}
          <Select
            id={"select-lot-width"}
            options={getLotWidthOptions()}
            value={apiContext.lotWidth}
            onChange={(val) => apiContext.setLotWidth(val)}
          />
          {userContext.landType === LandType.VACANT_BLOCK && (
            <>
              <Select
                id={"select-land-situation"}
                options={getLandSituationOptions()}
                value={
                  userContext.landSituation ? userContext.landSituation : ""
                }
                onChange={(val) => onChangeLandSituation(val)}
              />
              {userContext.landSituation === LAND_SITUATION_UNREGISTERED && (
                <DateInput
                  placeholder={"Expected date of registration"}
                  value={userContext.landRegistrationDate}
                  onChange={(value) =>
                    userContext.setLandRegistrationDate(value)
                  }
                  onScreenKeyboard={userContext.isKioskMode}
                />
              )}
            </>
          )}
        </div>
      </form>
    </div>
  );
});

export default YourLandScreen;
